(() => {
class HyraReferenceGalleryController {
  constructor(owner) {
    this.owner = owner;
    this.track = owner.querySelector('.hyra-gallery-track');
    this.thumbnailViewport = owner.querySelector('[id^="GalleryThumbnails"]');
    this.thumbnails = this.thumbnailViewport?.querySelector('ul');
    this.pagination = owner.querySelector('[data-hyra-gallery-pagination]');
    this.desktop = window.matchMedia('(min-width: 769px)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.items = [];
    this.index = -1;
  }

  connect() {
    if (this.abort || !this.track) return;
    this.abort = new AbortController();
    const on = (target, event, handler, options = {}) => target?.addEventListener(event, handler, { ...options, signal: this.abort.signal });
    on(this.owner, 'click', (event) => this.onClick(event));
    on(this.owner, 'keydown', (event) => this.onKeydown(event));
    on(this.track, 'scroll', () => {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        if (!this.pendingItem) this.activate(this.nearest(this.track, this.items));
        this.queueSettle();
      });
    }, { passive: true });
    on(this.track, 'wheel', () => { this.cancelPending(); this.queueSettle(); }, { passive: true });
    on(this.track, 'touchstart', (event) => {
      this.cancelPending();
      this.touching = true;
      const touch = event.touches[0];
      this.touchStart = { x: touch.clientX, y: touch.clientY, index: this.index };
    }, { passive: true });
    on(this.track, 'touchend', (event) => {
      this.touching = false;
      if (!this.touchStart) return;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - this.touchStart.x;
      const dy = touch.clientY - this.touchStart.y;
      if (Math.abs(dx) > 12) this.ignoreClickUntil = Date.now() + 400;
      if (!this.desktop.matches && Math.abs(dx) > 65 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        if (this.touchStart.index === 0 && dx > 0) this.go(this.items.length - 1, false, true);
        else if (this.touchStart.index === this.items.length - 1 && dx < 0) this.go(0, false, true);
      }
      this.touchStart = null;
      this.queueSettle();
    }, { passive: true });
    on(this.track, 'touchcancel', () => { this.touchStart = null; this.touching = false; this.queueSettle(); });
    on(this.owner, 'pointermove', (event) => {
      if (!this.zoomed) return;
      this.pointer = { x: event.clientX, y: event.clientY };
      if (!this.panFrame) this.panFrame = requestAnimationFrame(() => this.panZoom());
    }, { passive: true });
    on(this.desktop, 'change', () => { this.resetZoom(); this.go(this.index, false); });
    this.resize = new ResizeObserver(() => {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => this.go(this.index, false));
    });
    this.resize.observe(this.track);
    this.refresh();
    this.go(Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active'))), false);
  }

  disconnect() {
    this.abort?.abort();
    this.abort = null;
    this.resize?.disconnect();
    clearTimeout(this.settleTimer);
    this.pendingItem = null;
    ['scrollFrame', 'panFrame', 'resizeFrame', 'modalFrame'].forEach((key) => cancelAnimationFrame(this[key]));
    this.resetZoom();
    this.closeModal(false);
  }

  refresh() {
    this.items = Array.from(this.track.children).filter((item) => item.matches('li[data-media-id]') && !item.hidden && getComputedStyle(item).display !== 'none');
    const signature = this.items.map((item) => `${item.dataset.mediaId}:${item.dataset.thumbnailSrc || ''}`).join('|');
    if (signature !== this.signature) {
      this.signature = signature;
      const thumbnailNodes = new Map(Array.from(this.thumbnails?.children || []).map((li) => [li.dataset.target, li]));
      this.pagination?.replaceChildren();
      this.items.forEach((item, index) => {
        const label = item.dataset.mediaLabel || `Image ${index + 1}`;
        if (this.thumbnails) {
          let li = thumbnailNodes.get(item.dataset.mediaId);
          if (!li) {
            li = document.createElement('li');
            li.dataset.target = item.dataset.mediaId;
            const button = this.button(label, 'hyra-gallery-thumbnail');
            button.append(this.thumbnailImage(item));
            if (item.dataset.mediaType && item.dataset.mediaType !== 'image') {
              const mark = document.createElement('span');
              mark.className = 'hyra-gallery-video-mark';
              mark.setAttribute('aria-hidden', 'true');
              mark.textContent = '▶';
              button.append(mark);
            }
            li.append(button);
          }
          thumbnailNodes.delete(item.dataset.mediaId);
          li.dataset.mediaPosition = index + 1;
          const button = li.querySelector('button');
          button.classList.add('hyra-gallery-thumbnail');
          button.setAttribute('aria-label', label);
          button.setAttribute('aria-controls', this.track.id);
          button.dataset.hyraGalleryIndex = index;
          li.querySelector('img').draggable = false;
          // Keep loaded server-rendered images when initializing or changing variants.
          const next = this.thumbnails.children[index];
          if (next !== li) this.thumbnails.insertBefore(li, next || null);
        }
        if (this.pagination) {
          const button = this.button(`View ${label.toLowerCase()}, ${index + 1} of ${this.items.length}`, 'hyra-gallery-progress');
          button.dataset.hyraGalleryIndex = index;
          this.pagination.append(button);
        }
      });
      thumbnailNodes.forEach((li) => li.remove());
    }
    this.items.forEach((item) => item.querySelectorAll('img').forEach((img) => { img.draggable = false; }));
    const single = this.items.length < 2;
    this.owner.querySelectorAll('[data-hyra-gallery-previous], [data-hyra-gallery-next]').forEach((button) => { button.disabled = single; });
    if (this.pagination) this.pagination.hidden = single;
    this.index = -1;
  }

  button(label, className) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.setAttribute('aria-label', label);
    return button;
  }

  thumbnailImage(item) {
    const img = document.createElement('img');
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.draggable = false;
    if (item.dataset.thumbnailSrcset) {
      img.sizes = '55px';
      img.srcset = item.dataset.thumbnailSrcset;
    }
    img.src = item.dataset.thumbnailSrc || item.querySelector('img')?.currentSrc || '';
    return img;
  }

  nearest(track, items) {
    let best = 0;
    let distance = Infinity;
    items.forEach((item, index) => {
      const next = Math.abs(this.position(track, item) - track.scrollLeft);
      if (next < distance) { distance = next; best = index; }
    });
    return best;
  }

  position(track, item) {
    // Sibling layout offsets stay stable during compositor-driven touch momentum.
    return item.offsetLeft - (track.firstElementChild?.offsetLeft || 0);
  }

  go(index, smooth = true, edgeWrap = false) {
    if (!this.items.length) return;
    index = (index + this.items.length) % this.items.length;
    this.pendingItem = this.items[index];
    this.pendingUntil = Date.now() + (edgeWrap ? 650 : 0);
    this.activate(index);
    this.track.scrollTo({ left: this.position(this.track, this.items[index]), behavior: smooth && !this.reducedMotion.matches ? 'smooth' : 'instant' });
    this.queueSettle();
  }

  cancelPending() {
    this.pendingItem = null;
    this.pendingUntil = 0;
    clearTimeout(this.settleTimer);
    this.activate(this.nearest(this.track, this.items));
  }

  queueSettle() {
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => {
      if (this.touching || !this.abort || !this.items.length) return;
      const targetIndex = this.items.indexOf(this.pendingItem);
      if (targetIndex >= 0) {
        const left = this.position(this.track, this.pendingItem);
        // A released touch can still have momentum after an edge-wrap jump.
        if (Math.abs(this.track.scrollLeft - left) > 1) {
          this.track.scrollTo({ left, behavior: 'instant' });
          this.queueSettle();
          return;
        }
        if (Date.now() < this.pendingUntil) { this.queueSettle(); return; }
        this.pendingItem = null;
        this.activate(targetIndex);
      } else this.activate(this.nearest(this.track, this.items));
      const active = this.items[this.index];
      if (active && active !== this.settledItem) {
        this.settledItem = active;
        this.owner.playActiveMedia(active);
      }
    }, 160);
  }

  activate(index) {
    if (!this.items[index] || index === this.index) return;
    this.resetZoom();
    this.index = index;
    this.items.forEach((item, i) => {
      item.classList.toggle('is-active', i === index);
      item.inert = i !== index;
      item.setAttribute('aria-hidden', String(i !== index));
    });
    [this.thumbnails, this.pagination].forEach((container) => container?.querySelectorAll('button').forEach((button, i) => {
      if (i === index) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
      button.tabIndex = i === index ? 0 : -1;
    }));
    const thumb = this.thumbnails?.children[index];
    if (thumb) {
      const box = this.thumbnailViewport.getBoundingClientRect();
      const rect = thumb.getBoundingClientRect();
      if (this.desktop.matches && (rect.top < box.top || rect.bottom > box.bottom)) this.thumbnailViewport.scrollTo({ top: this.thumbnailViewport.scrollTop + rect.top - box.top - (box.height - rect.height) / 2, behavior: 'instant' });
    }
    const status = this.owner.querySelector('[id^="GalleryStatus"]');
    if (status) status.textContent = `${this.items[index].dataset.mediaLabel || 'Image'}, ${index + 1} of ${this.items.length}`;
  }

  setActiveMedia(mediaId, prepend) {
    this.resetZoom();
    this.pendingItem = null;
    clearTimeout(this.settleTimer);
    const target = Array.from(this.track.children).find((item) => item.dataset.mediaId === mediaId);
    if (prepend && target && this.track.firstElementChild !== target) this.track.prepend(target);
    this.refresh();
    this.go(Math.max(0, this.items.indexOf(target)), false);
  }

  onClick(event) {
    if (Date.now() < (this.ignoreClickUntil || 0)) { event.preventDefault(); return; }
    const target = event.target.closest('button');
    if (!target) return;
    if (target.hasAttribute('data-hyra-gallery-previous')) this.go(this.index - 1);
    else if (target.hasAttribute('data-hyra-gallery-next')) this.go(this.index + 1);
    else if (target.hasAttribute('data-hyra-gallery-index')) this.go(Number(target.dataset.hyraGalleryIndex));
    else if (target.hasAttribute('data-hyra-gallery-zoom')) {
      const item = target.closest('li[data-media-id]');
      if (this.desktop.matches) this.toggleZoom(item, event);
      else this.openModal(item, target);
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.zoomed) { this.resetZoom(); event.preventDefault(); return; }
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key) || event.target.closest('video, iframe, product-model, deferred-media, input, select, textarea')) return;
    const button = event.target.closest('button');
    if (!button) return;
    event.preventDefault();
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.items.length - 1 : this.index + (['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1);
    this.go(next);
    if (this.thumbnails?.contains(button)) this.thumbnails.querySelector('[aria-current]')?.focus({ preventScroll: true });
    else if (this.pagination?.contains(button)) this.pagination.querySelector('[aria-current]')?.focus({ preventScroll: true });
    else if (button.hasAttribute('data-hyra-gallery-zoom')) this.items[this.index]?.querySelector('[data-hyra-gallery-zoom]')?.focus({ preventScroll: true });
  }

  toggleZoom(item, event) {
    if (this.zoomed) { this.resetZoom(); return; }
    const image = item?.querySelector('[data-hyra-gallery-image]');
    if (!image) return;
    this.zoomed = { item, image, transform: image.style.transform, origin: image.style.transformOrigin, sizes: image.getAttribute('sizes') };
    item.classList.add('is-zoomed');
    item.querySelector('[data-hyra-gallery-zoom]')?.setAttribute('aria-pressed', 'true');
    image.style.transformOrigin = 'center center';
    image.sizes = '100vw';
    const rect = item.getBoundingClientRect();
    this.pointer = { x: event.detail ? event.clientX : rect.left + rect.width / 2, y: event.detail ? event.clientY : rect.top + rect.height / 2 };
    this.panZoom();
  }

  panZoom() {
    this.panFrame = null;
    if (!this.zoomed) return;
    const rect = this.zoomed.item.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (this.pointer.x - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (this.pointer.y - rect.top) / rect.height));
    this.zoomed.image.style.transform = `translate3d(${(0.5 - x) * rect.width}px, ${(0.5 - y) * rect.height}px, 0) scale(2)`;
  }

  resetZoom() {
    cancelAnimationFrame(this.panFrame);
    this.panFrame = null;
    if (!this.zoomed) return;
    const { item, image, transform, origin, sizes } = this.zoomed;
    item.classList.remove('is-zoomed');
    item.querySelector('[data-hyra-gallery-zoom]')?.setAttribute('aria-pressed', 'false');
    image.style.transform = transform;
    image.style.transformOrigin = origin;
    if (sizes === null) image.removeAttribute('sizes'); else image.setAttribute('sizes', sizes);
    this.zoomed = null;
  }

  openModal(item, opener) {
    if (this.modal) return;
    const sources = this.items.map((element) => ({ element, image: element.querySelector('[data-hyra-gallery-image]') })).filter((source) => source.image);
    const initial = sources.findIndex((source) => source.element === item);
    if (initial < 0) return;
    this.owner.playActiveMedia(item);
    const dialog = document.createElement('dialog');
    dialog.className = 'hyra-gallery-modal';
    dialog.setAttribute('aria-label', 'Product image gallery');
    const close = this.button('Close image gallery', 'hyra-gallery-modal__close');
    close.textContent = '×';
    const track = document.createElement('div');
    track.className = 'hyra-gallery-modal__track';
    const thumbs = document.createElement('div');
    thumbs.className = 'hyra-gallery-modal__thumbnails';
    sources.forEach(({ element, image }, index) => {
      const slide = document.createElement('div');
      slide.className = 'hyra-gallery-modal__slide';
      const large = document.createElement('img');
      large.className = 'hyra-gallery-modal__image';
      large.loading = index === initial ? 'eager' : 'lazy';
      large.decoding = 'async';
      if (image.srcset) { large.sizes = '100vw'; large.srcset = image.srcset; }
      large.src = image.currentSrc || image.src;
      large.alt = image.alt;
      large.draggable = false;
      slide.append(large); track.append(slide);
      const thumb = this.button(`View image ${index + 1} of ${sources.length}`, 'hyra-gallery-modal__thumbnail');
      const small = this.thumbnailImage(element);
      thumb.append(small); thumbs.append(thumb);
      thumb.addEventListener('click', () => navigate(index));
    });
    let current = -1;
    const select = (index) => {
      if (index === current) return;
      current = index;
      Array.from(thumbs.children).forEach((button, i) => {
        if (i === index) button.setAttribute('aria-current', 'true'); else button.removeAttribute('aria-current');
      });
      const thumb = thumbs.children[index];
      if (thumb) thumbs.scrollTo({ left: thumb.offsetLeft - thumbs.offsetLeft - (thumbs.clientWidth - thumb.clientWidth) / 2, behavior: 'instant' });
    };
    const navigate = (index) => {
      index = Math.max(0, Math.min(sources.length - 1, index));
      select(index);
      track.scrollTo({ left: this.position(track, track.children[index]), behavior: 'instant' });
    };
    dialog.append(close, track, thumbs);
    document.body.append(dialog);
    this.modal = { dialog, opener, bodyOverflow: document.body.style.overflow, htmlOverflow: document.documentElement.style.overflow };
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    close.addEventListener('click', () => this.closeModal());
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.closeModal(); });
    dialog.addEventListener('close', () => this.closeModal());
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { event.preventDefault(); this.closeModal(); }
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); navigate(current + (event.key === 'ArrowLeft' ? -1 : 1)); }
      else if (event.key === 'Tab') {
        const focusable = [close, ...thumbs.children];
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    });
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.modalFrame);
      this.modalFrame = requestAnimationFrame(() => select(this.nearest(track, Array.from(track.children))));
    }, { passive: true });
    dialog.showModal();
    navigate(initial);
    close.focus({ preventScroll: true });
  }

  closeModal(restoreFocus = true) {
    if (!this.modal) return;
    const { dialog, opener, bodyOverflow, htmlOverflow } = this.modal;
    this.modal = null;
    cancelAnimationFrame(this.modalFrame);
    dialog.close();
    dialog.remove();
    document.body.style.overflow = bodyOverflow;
    document.documentElement.style.overflow = htmlOverflow;
    if (restoreFocus && opener.isConnected) opener.focus({ preventScroll: true });
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        if (this.hasAttribute('data-hyra-reference-gallery')) {
          this.hyraController = new HyraReferenceGalleryController(this);
          return;
        }
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() { this.hyraController?.connect(); }

      disconnectedCallback() { this.hyraController?.disconnect(); }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (this.hyraController) return this.hyraController.setActiveMedia(mediaId, prepend);
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

})();
